import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useAbility } from 'permissions/hooks/useAbility';
import { useMemo } from 'react';
import { CellConfiguration, CellLoader } from '../Components/Cells/CellLoader';
import { CollapseAllButton } from '../Components/Buttons/CollapseAll';
import { ActionCell } from '../Components/Cells/ActionCell';
import { HeaderCell } from '../Components/Header/HeaderCell';
import { Action, ColumnConfiguration, GridColDef, HeaderAction } from '../models/configurableTable';
import { actionPermissionFilter } from '../utils/actionsFiltering';
import { getOnlyColumnsToDisplayed } from '../utils/ColumnConfiguration.util';
import { HeaderActionCell } from '../Components/Cells/HeaderActionCell';
import { isEmpty } from 'lodash';
import { useRootFontScale } from '../Components/Base/useRootFontScale';
import { DEFAULT_COLUMN_MAX_WIDTH } from '../../Sim/Inventory/constants/columnConfigurationsConstants';

type UsePrepareConfigurationProps<T> = {
  cellConfiguration?: CellConfiguration;
  detailPanelEnabled: boolean;
  configuration: ColumnConfiguration[];
  actions?: Action[];
  headerActions?: HeaderAction<T>[];
  cellProps: any;
  selectDialog: (dialogId: string, data: any) => void;
  fillerWidth?: number;
};

export const configurableCellRender =
  (cellConfiguration: any, cellProps?: any) => (params: GridRenderCellParams) => {
    return <CellLoader {...params} cellConfiguration={cellConfiguration} cellProps={cellProps} />;
  };

const getColumnSizes = (
  {
    width = 100,
    maxWidth = DEFAULT_COLUMN_MAX_WIDTH,
    minWidth = 50,
  }: { width?: number; maxWidth?: number; minWidth?: number },
  fontScale: number,
) => ({
  width: width * fontScale,
  maxWidth: maxWidth * fontScale,
  minWidth: minWidth * fontScale,
});

export const usePrepareConfiguration = <T,>({
  configuration,
  actions,
  headerActions,
  cellProps,
  cellConfiguration,
  selectDialog,
  detailPanelEnabled,
  fillerWidth,
}: UsePrepareConfigurationProps<T>) => {
  const ability = useAbility();

  const fontScale = useRootFontScale();
  const columnsConfiguration = useMemo(() => {
    const definedcolumns = getOnlyColumnsToDisplayed(configuration, { ability }).map((column) => {
      const columnDef: GridColDef = {
        ...column,
        ...getColumnSizes(column, fontScale),
        renderCell: configurableCellRender(cellConfiguration, cellProps),
        renderHeader: (props) => {
          if (column.HeaderComponent) {
            return (
              <column.HeaderComponent
                primaryText={props.colDef.headerName || ''}
                secondaryText={props.colDef.subField?.headerName}
                colDef={props.colDef}
              />
            );
          }
          return (
            <HeaderCell
              primaryText={props.colDef.headerName || ''}
              secondaryText={props.colDef.subField?.headerName}
              colDef={props.colDef}
            />
          );
        },
      };

      return columnDef;
    });

    let columns = [];

    if (detailPanelEnabled) {
      columns.push({
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 54 * fontScale,
        renderHeader: () => {
          return <CollapseAllButton />;
        },
      });
    }

    columns = [...columns, ...definedcolumns];

    if (detailPanelEnabled) {
      const filler: GridColDef = {
        field: 'filler',
        headerName: '',
        display: 'flex',
        sortable: false,
        width: fillerWidth,
      };
      columns.push(filler);
    }

    if (actions) {
      //Add callback for type component
      const mappedAction = actions.filter(actionPermissionFilter(ability)).map((action: any) => {
        if (action.type === 'dialog') {
          return {
            ...action,
            actionCallback: (data: any) => selectDialog(action.id, data),
          };
        }

        return action;
      });

      const actionsColDef: GridColDef = {
        field: 'actions',
        headerName: '',
        resizable: false,
        width: 60 * fontScale,
        minWidth: 60 * fontScale,
        maxWidth: 60 * fontScale,

        renderCell: (props) => {
          return <ActionCell actions={mappedAction} data={props.row} />;
        },
        sortable: false,
        pinnable: false,
      };

      if (headerActions && !isEmpty(headerActions)) {
        const mappedHeaderActions = headerActions
          .filter(actionPermissionFilter(ability))
          .map((action: any) => {
            if (action.type === 'dialog') {
              return {
                ...action,
                actionCallback: () => selectDialog(action.id, action.data ?? true),
              };
            }

            return action;
          });

        actionsColDef.renderHeader = () => {
          return <HeaderActionCell actions={mappedHeaderActions} />;
        };
      }

      columns.push(actionsColDef);
    }

    return columns;
  }, [configuration, actions, headerActions, detailPanelEnabled, fillerWidth, fontScale]);

  return columnsConfiguration;
};
